<template>
    
      <form v-on:submit.prevent="onSubmitted" class="p-1">
        <div class="space-y-6">
          <div class="space-y-1">
            <h1 class="text-lg leading-6 font-medium text-gray-900">
              {{ title }}
            </h1>
            <p class="text-sm leading-5 text-gray-500">
              {{ description }}
            </p>
          </div>

          <div class="space-y-1">
            <label
              for="project_name"
              class="block text-sm font-medium leading-5 text-gray-700"
            >
              Narrative name
              <span
                :class="{ 'valid-message' : markNameAsValid, 'invalid-message' : markNameAsInvalid }"
              >
                {{ fullPath }}
              </span>
            </label>
            <div class="rounded-md shadow-sm">
              <input
                id="narrativeName"
                v-model="updatedNarrative.name"
                class="form-input block w-full transition duration-150
                ease-in-out sm:text-sm sm:leading-5"
                :class="{ 'valid' : markNameAsValid, 'invalid' : markNameAsInvalid }"
                spellcheck="false"
                value=""
              />
            </div>

            <div v-if="checkingName" class="message-hint">
              Checking name...
            </div>
            <div v-if="markNameAsInvalid" class="message-hint invalid-message">
              {{ invalidNameHint }}
            </div>
            <div v-if="markNameAsValid" class="message-hint valid-message">
              This name is available.
            </div>
            <p class="message-hint text-gray-500">
              * The entered name is preprocessed to match the required format.
              <span v-if="!isNewNarrative">
                If narrative name is changed, the associated repository's name will be changed accordingly.
              </span>
            </p>
          </div>

          <div class="space-y-1">
            <label
              for="description"
              class="block text-sm font-medium leading-5 text-gray-700"
            >
              Description
            </label>
            <div class="rounded-md shadow-sm">
              <textarea
                id="description"
                v-model="updatedNarrative.description"
                rows="3"
                class="form-textarea block w-full transition duration-150
              ease-in-out sm:text-sm sm:leading-5"
              ></textarea>
            </div>
          </div>

          <fieldset>
            <legend class="text-sm leading-5 font-medium text-gray-900">
              Privacy
            </legend>

            <div class="mt-1 bg-white rounded-md shadow-sm -space-y-px">
              <!-- On: "bg-light-blue-50 border-light-blue-200 z-10", Off: "border-gray-200" -->
              <div class="relative border rounded-tl-md rounded-tr-md p-4 flex">
                <div class="flex items-center h-5">
                  <input
                    id="settings-option-0"
                    value="public"
                    v-model="updatedNarrative.privacySetting"
                    name="privacySetting"
                    type="radio"
                    class="form-radio h-4 w-4 text-light-blue-600 transition
                    duration-150 ease-in-out cursor-pointer"
                    checked
                  />
                </div>
                <label
                  for="settings-option-0"
                  class="ml-3 flex flex-col cursor-pointer"
                >
                  <!-- On: "text-light-blue-900", Off: "text-gray-900" -->
                  <span class="block text-sm leading-5 font-medium">
                    Public access
                  </span>
                  <!-- On: "text-light-blue-700", Off: "text-gray-500" -->
                  <span class="block text-sm leading-5">
                    This data narrative would be available to anyone who has the
                    link
                  </span>
                </label>
              </div>

              <!-- On: "bg-light-blue-50 border-light-blue-200 z-10", Off: "border-gray-200" -->
              <div class="relative border border-gray-200 p-4 flex">
                <div class="flex items-center h-5">
                  <input
                    id="settings-option-1"
                    name="privacySetting"
                    v-model="updatedNarrative.privacySetting"
                    value="private_to_members"
                    type="radio"
                    class="form-radio h-4 w-4 text-light-blue-600 transition
                  duration-150 ease-in-out cursor-pointer"
                  />
                </div>
                <label
                  for="settings-option-1"
                  class="ml-3 flex flex-col cursor-pointer"
                >
                  <!-- On: "text-light-blue-900", Off: "text-gray-900" -->
                  <span class="block text-sm leading-5 font-medium">
                    Private to narrative members
                  </span>
                  <!-- On: "text-light-blue-700", Off: "text-gray-500" -->
                  <span class="block text-sm leading-5">
                    Only members of this narrative would be able to access
                  </span>
                </label>
              </div>

              <!-- On: "bg-light-blue-50 border-light-blue-200 z-10", Off: "border-gray-200" -->
              <div
                class="relative border border-gray-200 rounded-bl-md rounded-br-md p-4 flex"
              >
                <div class="flex items-center h-5">
                  <input
                    id="settings-option-2"
                    value="private_to_me"
                    v-model="updatedNarrative.privacySetting"
                    name="privacySetting"
                    type="radio"
                    class="form-radio h-4 w-4 text-light-blue-600 transition
                  duration-150 ease-in-out cursor-pointer"
                  />
                </div>
                <label
                  for="settings-option-2"
                  class="ml-3 flex flex-col cursor-pointer"
                >
                  <!-- On: "text-light-blue-900", Off: "text-gray-900" -->
                  <span class="block text-sm leading-5 font-medium">
                    Private to you
                  </span>
                  <!-- On: "text-light-blue-700", Off: "text-gray-500" -->
                  <span class="block text-sm leading-5">
                    You are the only one able to access this data narrative
                  </span>
                </label>
              </div>
            </div>
          </fieldset>

          <div class="space-y-1">
            <label
              for="tags"
              class="block text-sm font-medium leading-5 text-gray-700"
            >
              Tags
              <span class="text-gray-400"
                >(comma separated — e.g.: sensor, health)</span
              >
            </label>
            <div class="rounded-md shadow-sm">
              <input
                id="tags"
                v-model="updatedNarrative.tags"
                class="form-input block w-full transition duration-150
                ease-in-out sm:text-sm sm:leading-5"
              />
            </div>
          </div>

          <div class="flex justify-end">
            <button type="submit">
                <c-button size='small' :loading="loading" :disabled="!isValidNarrativeName || checkingName">{{ submitButtonText }}</c-button>
            </button>
          </div>
        </div>
      </form>
    
</template>

<script>
import { cleanName, debounce, reportError, EventBus } from "@/utils.js";
import CButton from '@/components/Core/CButton.vue'


export default {
  components: {
    CButton
  },  
  props: ["username", "title", "description", "submitButtonText", "loading", "narrative", "isNewNarrative"],

  data() {
    return {
      updatedNarrative: Object.assign({}, this.narrative), // copy the original narrative
      checkingName: false,
      isValidNarrativeName: true,
      invalidNameHint: '',
      checksCounter: 0
    };
  },

  watch: {},

  methods: {
    onSubmitted() {
      if (!this.updatedNarrative.name) {
        this.isValidNarrativeName = false;
        this.invalidNameHint = 'Please enter narrative name.';
        this.checkingName = false;
        this.checksCounter++;
      } else {
        this.$emit('save', this.updatedNarrative);
      }
    },

    narrativeCreated() {
      var narrativeName = this.$store.state.narratives.current.name;
      this.$router.push({
        name: "Narrative",
        params: { username: this.username, narrativeName: narrativeName },
      });
    },

    createNarrative() {
      let details = { username: this.username, narrative: this.narrative };
      this.$store.dispatch("narratives/create", details);
    }
  },

  computed: {
    fullPath() {
      if (this.updatedNarrative.name.length > 0) {
        return `(${this.username}/narratives/${cleanName(
          this.updatedNarrative.name
        )})`;
      } else {
        return "";
      }
    },

    markNameAsValid() {
      return this.checksCounter > 0 && !this.checkingName && this.isValidNarrativeName;
    },

    markNameAsInvalid() {
      return this.checksCounter > 0 && !this.checkingName && !this.isValidNarrativeName;
    }
  },

  mounted() {
    // HOOKS TO CHECK THE DATASET NAME
    var validateName = debounce(() => {
      this.updatedNarrative.name = cleanName(this.updatedNarrative.name);
      if (this.updatedNarrative.name === this.narrative.name && !this.isNewNarrative) {
          // Name is unchanged
          this.isValidNarrativeName = true;
          this.checkingName = false;
      } else if (!this.updatedNarrative.name) {
          // Name is empty
          if (this.checksCounter > 0) {
            this.isValidNarrativeName = false;
            this.invalidNameHint = 'Please enter narrative name.';
            this.checkingName = false;
          }
      } else {
        // Checking if name is available
        this.checksCounter++;
        this.checkingName = true;
        this.$samsa
        .isValidNarrativeName(this.updatedNarrative.name, this.username)
        .then((res) => {
          this.isValidNarrativeName = res.data;
          if (!this.isValidNarrativeName) {
              this.invalidNameHint = 'The given narrative name is already taken. Please enter a different name.';
          }
          this.checkingName = false;
        })
        .catch((err) => {
          this.checkingName = false;
          this.isValidNarrativeName = false;
          this.invalidNameHint = 'Failed to validate given name. Please check your network connection.';
          reportError(err);
        });
      }
    }, 1000);
    const name = document.querySelector("#narrativeName");
    name.addEventListener("keyup", validateName);
    validateName();
  },
};

</script>

<style>
  input.invalid, input.invalid:focus {
    border-color: #dc3545;
  }

  input.invalid:focus {
    box-shadow: 0 0 5px #dc3545;
  }

  input.valid, input.valid:focus {
    border-color: #198754;
  }

  input.valid:focus {
    box-shadow: 0 0 5px #198754;
  }

  .message-hint {
    margin-top: .25rem;
    font-size: .875em;
  }

  .invalid-message {
    color: #dc3545;
  }

  .valid-message {
    color: #198754;
  }
</style>
